import { createContext, use, type ReactNode } from 'react'

import type { ColumnHighlight } from './definition'
import type { FieldFilter } from './search-state'

/** How a term finds text: anywhere, at its start, or as the whole of it. */
export type HighlightMatch = 'contains' | 'startsWith' | 'equals'

/** Text a search looked for, and how. */
export interface HighlightTerm {
  readonly text: string
  readonly match: HighlightMatch
}

// Ignores case but not accents, as the API's collation does; compared in place, so no index moves
// as it would where lowering a letter's case changes its length. A fixed locale, not the
// browser's, so a Turkish one still reads "MI" as "mi", as the API does.
const collator = new Intl.Collator('en', { sensitivity: 'accent', usage: 'search' })

/** Whether `term` is found in `text` at `at`, as the API matches text. */
export function matchesAt(text: string, at: number, term: string): boolean {
  return collator.compare(text.slice(at, at + term.length), term) === 0
}

/** The `[start, end)` ranges of `text` the terms find, in order, overlapping ones merged. */
export function highlightRanges(text: string, terms: readonly HighlightTerm[]): readonly (readonly [number, number])[] {
  const found: [number, number][] = []
  for (const term of terms) {
    const needle = term.text.trim()
    if (needle === '' || needle.length > text.length) {
      continue
    }
    if (term.match === 'equals') {
      if (needle.length === text.length && matchesAt(text, 0, needle)) {
        found.push([0, text.length])
      }
    } else if (term.match === 'startsWith') {
      if (matchesAt(text, 0, needle)) {
        found.push([0, needle.length])
      }
    } else {
      let at = 0
      while (at <= text.length - needle.length) {
        // Overlapping hits, as "ana" twice in "Banana", merge below.
        if (matchesAt(text, at, needle)) {
          found.push([at, at + needle.length])
        }
        at += 1
      }
    }
  }

  found.sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = []
  for (const [start, end] of found) {
    const last = merged.at(-1)
    if (last !== undefined && start <= last[1]) {
      last[1] = Math.max(last[1], end)
    } else {
      merged.push([start, end])
    }
  }
  return merged
}

/** What a filter looked for: a negated one marks nothing, as a row it kept did not match it. */
function filterTerms(filter: FieldFilter): readonly HighlightTerm[] {
  const [value = ''] = filter.values
  switch (filter.operator) {
    case 'contains':
      return [{ text: value, match: 'contains' }]
    case 'startsWith':
      return [{ text: value, match: 'startsWith' }]
    case 'equals':
      return [{ text: value, match: 'equals' }]
    case 'in':
      return filter.values.map((text) => ({ text, match: 'equals' }))
    default:
      return []
  }
}

/** The terms a column marks: the search box's text, found anywhere, and its filter fields'. */
export function columnTerms(highlight: ColumnHighlight | undefined, q: string, filters: readonly FieldFilter[]): readonly HighlightTerm[] {
  if (highlight === undefined) {
    return []
  }
  const text = q.trim()
  const fields = highlight.fields ?? []
  return [
    ...(highlight.q === true && text !== '' ? [{ text, match: 'contains' as const }] : []),
    ...filters.filter((filter) => fields.includes(filter.field)).flatMap(filterTerms),
  ]
}

const HighlightContext = createContext<readonly HighlightTerm[]>([])

/** A column's cell with the terms it marks; a cell that is plain text is marked as it is. */
export function HighlightScope(props: { readonly terms: readonly HighlightTerm[]; readonly children: ReactNode }) {
  return (
    <HighlightContext value={props.terms}>
      {typeof props.children === 'string' ? <Highlight text={props.children} /> : props.children}
    </HighlightContext>
  )
}

/**
 * Text with what the search found in it marked (the mockups' `Highlight.jsx`), so a viewer sees
 * why a row is listed: `terms`, else its column's. The string is split in code, never read as
 * markup, and `<mark>` keeps the text, so a link's name stays the same.
 */
export function Highlight(props: { readonly text: string; readonly terms?: readonly HighlightTerm[] }) {
  const scoped = use(HighlightContext)
  const { text } = props
  const ranges = highlightRanges(text, props.terms ?? scoped)
  if (ranges.length === 0) {
    return text
  }
  const parts: ReactNode[] = []
  let at = 0
  for (const [start, end] of ranges) {
    if (start > at) {
      parts.push(text.slice(at, start))
    }
    parts.push(
      <mark key={start} className="rounded-xs bg-accent-light px-0.5 font-semibold text-text-primary">
        {text.slice(start, end)}
      </mark>,
    )
    at = end
  }
  if (at < text.length) {
    parts.push(text.slice(at))
  }
  // One box: in a flex parent, such as a link sized as a target, each mark would become a block
  // of its own, and the name would read "Love lace" rather than "Lovelace".
  return <span>{parts}</span>
}
